// Safe, lightweight Markdown: input is HTML-escaped first, then a small set of constructs is turned into tags.
// Supported: # / ## / ### headings, - • * lists, 1. lists, **bold**, *italic*, [text](url), bare links.
const esc = (s: string) =>
  s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

// Links are found in the RAW text and built from escaped parts; regexes never run over generated HTML.
// URLs stop at whitespace, brackets, quotes and angle brackets, so they cannot break out of the href attribute.
const URL_CHARS = String.raw`https?:\/\/[^\s()<>"'\x60\[\]]+`;
const LINK = new RegExp(String.raw`\[([^\]\n]+)\]\((${URL_CHARS})\)|(${URL_CHARS})`, 'g');
const anchor = (href: string, label: string) =>
  `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
const emphasis = (escaped: string) =>
  escaped
    // backslash escapes from the editor (\* \_ \# …) stay plain characters: as entities they match no rule below
    .replace(/\\([!-/:-@[-`{-~])/g, (_m, c: string) => `&#${c.charCodeAt(0)};`)
    // underline written by the editor as <u>…</u> (escaped above to &lt;u&gt;); only this exact tag, nothing else
    .replace(/&lt;u&gt;(.+?)&lt;\/u&gt;/g, '<u>$1</u>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<s>$1</s>') // strikethrough
    .replace(/\+\+([^+]+)\+\+/g, '<u>$1</u>'); // underline

function inline(raw: string) {
  let out = '',
    text = '',
    last = 0;
  for (const m of raw.matchAll(LINK)) {
    text += raw.slice(last, m.index);
    last = m.index + m[0].length;
    if (m[1] !== undefined) {
      out += emphasis(esc(text)) + anchor(m[2], emphasis(esc(m[1])));
    } else {
      // bare link: trailing punctuation belongs to the sentence, not to the URL
      const url = m[3].replace(/[.,;:!?]+$/, '');
      out += emphasis(esc(text)) + anchor(url, esc(url));
      last -= m[3].length - url.length;
    }
    text = '';
  }
  return out + emphasis(esc(text + raw.slice(last)));
}

/**
 * Simple HTML from the old Sportplaner (<b>, <strong>, <i>, <em>, <s>, <strike>, <del>, <br>, <p>) → Markdown.
 * <u> stays (the renderer knows it). Any other HTML is left alone and later shown as plain text.
 */
export const legacyHtml = (t: string) =>
  String(t || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<\/?p>/gi, '\n')
    .replace(/<(b|strong)>([\s\S]*?)<\/\1>/gi, '**$2**')
    .replace(/<(i|em)>([\s\S]*?)<\/\1>/gi, '*$2*')
    .replace(/<(s|strike|del)>([\s\S]*?)<\/\1>/gi, '~~$2~~')
    .replace(/<U>([\s\S]*?)<\/U>/g, '<u>$1</u>');

export function md(text: string): string {
  const lines = legacyHtml(text).replace(/\r/g, '').split('\n');
  let out = '',
    para: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
  const flushP = () => {
    if (para.length) {
      out += `<p>${para.map(inline).join('<br>')}</p>`;
      para = [];
    }
  };
  const flushL = () => {
    if (list) {
      out += `<${list.tag}>${list.items.map(i => `<li>${inline(i)}</li>`).join('')}</${list.tag}>`;
      list = null;
    }
  };
  for (const raw of lines) {
    const l = raw.trimEnd();
    let m: RegExpMatchArray | null;
    if (!l.trim()) {
      flushP();
      flushL();
      continue;
    }
    if ((m = l.match(/^\s*(#{1,3})\s+(.+)/))) {
      flushP();
      flushL();
      const lv = m[1].length + 2;
      out += `<h${lv}>${inline(m[2])}</h${lv}>`;
      continue;
    }
    if ((m = l.match(/^\s*[-*•–]\s+(.+)/))) {
      flushP();
      if (!list || list.tag !== 'ul') {
        flushL();
        list = { tag: 'ul', items: [] };
      }
      list.items.push(m[1]);
      continue;
    }
    if ((m = l.match(/^\s*\d+[.)]\s+(.+)/))) {
      flushP();
      if (!list || list.tag !== 'ol') {
        flushL();
        list = { tag: 'ol', items: [] };
      }
      list.items.push(m[1]);
      continue;
    }
    flushL();
    para.push(l.trim());
  }
  flushP();
  flushL();
  return out;
}

export const stripMd = (t: string) =>
  legacyHtml(t)
    .replace(/<\/?u>/gi, '')
    .replace(/^\s*#{1,3}\s+/gm, '')
    .replace(/^\s*(?:[-*•–]|\d+[.)])\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/\+\+([^+]+)\+\+/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
export const firstLine = (t: string) =>
  stripMd(t)
    .split(/\n+/)
    .map(s => s.trim())
    .filter(Boolean)
    .join(' · ');

/** Hover text for an event (title, time, place, description as plain text) – shown by the browser as a tooltip. */
export function eventTooltip(e: { title: string; description: string; location: string }, when: string, max = 600) {
  const desc = stripMd(e.description)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  const head = [e.title, [when, e.location].filter(Boolean).join(' · ')].filter(Boolean).join('\n');
  return desc ? `${head}\n\n${desc.length > max ? `${desc.slice(0, max).trimEnd()} …` : desc}` : head;
}
